import LegalLayout from "./LegalLayout";
import { LEGAL, PRODUCT, isPlaceholder } from "../../lib/legal-config";

/**
 * Allgemeine Geschäftsbedingungen für {PRODUCT.name}.
 *
 * Rechtliche Leitplanken:
 *   • § 305 BGB ff (Einbeziehung, Transparenz, Inhaltskontrolle)
 *   • § 312i BGB (Informationspflichten bei Fernabsatz; Dienst richtet sich auch
 *     an Verbraucher → Widerrufsrecht nach § 355 BGB ist zu beachten)
 *   • § 3 RDG (Rechtsdienstleistungs-Erlaubnispflicht) — wir erbringen KEINE
 *     Rechtsdienstleistung, der Disclaimer ist in den AGB doppelt verankert
 *   • § 16 Abs. 2 UWG (Verbot progressiver Kundenwerbung / Pyramidensystem) —
 *     unser Affiliate-Modell ist ein reines Kauf-bezogenes Referral-Modell ohne
 *     Anwerbe-Provisionen, daher zulässig
 *   • § 138 BGB (Sittenwidrigkeit / wucherische Klauseln)
 */
export default function AGB() {
  return (
    <LegalLayout title="Allgemeine Geschäftsbedingungen (AGB)" lastUpdated="2026-04-22">
      <p className="text-sm text-slate-400">
        Die folgenden AGB regeln das Nutzungsverhältnis zwischen{" "}
        {isPlaceholder(LEGAL.companyName)
          ? "[PLACEHOLDER Firmenname]"
          : LEGAL.companyName}{" "}
        (nachfolgend „<strong>Anbieter</strong>&quot;) und dem Nutzer (nachfolgend „
        <strong>Kunde</strong>&quot;).
      </p>

      <h2>§ 1 Geltungsbereich</h2>
      <p>
        (1) Diese AGB gelten für sämtliche Verträge zwischen dem Anbieter und dem Kunden über die
        Nutzung der Online-Plattform <strong>{PRODUCT.name}</strong> unter der Domain{" "}
        <strong>{PRODUCT.domain}</strong>.
      </p>
      <p>
        (2) Entgegenstehende oder abweichende Bedingungen des Kunden gelten nicht, es sei denn,
        der Anbieter stimmt ihrer Geltung ausdrücklich schriftlich zu.
      </p>

      <h2>§ 2 Leistungsbeschreibung</h2>
      <p>
        (1) {PRODUCT.name} ist ein <strong>technisches Simulationswerkzeug</strong>. Nach Eingabe
        oder Hochladen eines Sachverhalts berechnet ein KI-gestützter Juroren-Schwarm eine
        statistische Prognose über mögliche Gerichtsausgänge, Schadensersatzhöhen und
        Vergleichsoptionen.
      </p>
      <p>
        (2) <strong>Keine Rechtsberatung.</strong> Die Ergebnisse stellen ausdrücklich keine
        Rechtsdienstleistung im Sinne des § 2 Rechtsdienstleistungsgesetz (RDG) dar. Sie ersetzen
        insbesondere nicht die individuelle Beratung durch einen zugelassenen Rechtsanwalt. Der
        Kunde trifft Entscheidungen über die Geltendmachung von Ansprüchen oder die Verteidigung
        gegen Ansprüche eigenverantwortlich.
      </p>
      <p>
        (3) Der Anbieter schuldet keinen bestimmten Erfolg. Die Vorhersagen beruhen auf
        maschinellem Lernen und sind mit einer verbleibenden Unsicherheit behaftet, die der Kunde
        bei der Nutzung zu berücksichtigen hat.
      </p>

      <h2>§ 3 Vertragsschluss</h2>
      <p>
        (1) Das Angebot auf der Plattform stellt kein bindendes Angebot dar, sondern eine
        Aufforderung an den Kunden zur Abgabe eines Angebots.
      </p>
      <p>
        (2) Durch Anklicken der Schaltfläche „Kostenpflichtig bestellen&quot; bzw. einer
        gleichbedeutenden Schaltfläche gibt der Kunde ein verbindliches Angebot ab. Der Vertrag
        kommt mit Bestätigung durch den Anbieter (z.B. Rechnungs-E-Mail oder Freischaltung der
        Funktion) zustande.
      </p>

      <h2>§ 4 Preise und Zahlung</h2>
      <p>
        (1) Es gelten die zum Zeitpunkt der Bestellung auf der Plattform ausgewiesenen Preise.
        Sämtliche Preise enthalten die gesetzliche Umsatzsteuer, soweit nicht anders angegeben.
      </p>
      <p>
        (2) Die Zahlung erfolgt über den Drittanbieter Stripe. Für die Zahlungsabwicklung gelten
        zusätzlich die Allgemeinen Geschäftsbedingungen von Stripe Payments Europe, Ltd.
      </p>

      <h2>§ 5 Widerrufsrecht für Verbraucher</h2>
      <p>
        Verbraucher haben ein Widerrufsrecht nach § 355 BGB. Da die Leistung digital und sofort
        abrufbar bereitgestellt wird, <strong>erlischt das Widerrufsrecht nach § 356 Abs. 5 BGB</strong>,
        wenn der Kunde vor Vertragsausführung ausdrücklich zugestimmt hat, dass die Ausführung vor
        Ablauf der Widerrufsfrist beginnt, und dabei bestätigt hat, dass er sein Widerrufsrecht
        kennt. Der Anbieter wird diese Bestätigung im Bestellprozess einholen.
      </p>

      <h2>§ 6 Pflichten des Kunden</h2>
      <p>Der Kunde verpflichtet sich,</p>
      <ul>
        <li>
          nur solche Dokumente hochzuladen, zu deren Übermittlung er berechtigt ist; insbesondere
          keine Dokumente, die Rechte Dritter (Urheberrecht, Berufsgeheimnisse, Persönlichkeits-
          rechte) verletzen;
        </li>
        <li>
          keine personenbezogenen Daten Dritter hochzuladen, soweit hierfür keine Rechtsgrundlage
          besteht;
        </li>
        <li>
          keine Inhalte einzugeben, die volksverhetzend, beleidigend, gewaltverherrlichend,
          kinderpornografisch oder sonst strafbar sind;
        </li>
        <li>
          die Plattform nicht über automatisierte Scripts zu überlasten, außer soweit die
          dokumentierte API dies vorsieht (Rate-Limits: 10 Trials/Minute, 5 Uploads/Minute,
          20 Affiliate-Requests/Minute pro Account);
        </li>
        <li>
          seine Zugangsdaten geheim zu halten und einen Missbrauch unverzüglich an{" "}
          <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a> zu melden.
        </li>
      </ul>

      <h2>§ 7 Affiliate-Programm</h2>
      <p>
        (1) Der Anbieter betreibt ein dreistufiges Affiliate-Programm. Ein Affiliate erhält eine
        Provision, wenn ein von ihm geworbener Kunde (Tier 1), ein vom Tier 1 geworbener Kunde
        (Tier 2) oder ein vom Tier 2 geworbener Kunde (Tier 3) einen kostenpflichtigen Auftrag
        erteilt.
      </p>
      <p>
        (2) <strong>Keine Pyramiden- oder Schneeballsysteme:</strong> Eine Provision wird
        ausschließlich auf tatsächliche Produkt-Käufe ausgezahlt. Für das Anwerben weiterer
        Affiliates wird <strong>keine Vergütung</strong> gezahlt. Eine Eintrittsgebühr wird nicht
        erhoben. Das Programm entspricht damit den Vorgaben des § 16 Abs. 2 UWG.
      </p>
      <p>
        (3) Die konkreten Provisionssätze (derzeit 25 % auf Tier 1, 10 % auf Tier 2, 5 % auf
        Tier 3) können mit Wirkung für die Zukunft geändert werden; bereits entstandene
        Provisionen bleiben unberührt.
      </p>
      <p>
        (4) Provisionen werden einmal monatlich ausgezahlt, sofern der Auszahlungsbetrag mindestens
        20 EUR beträgt. Affiliates sind verpflichtet, die Provisionen nach eigenen
        steuerrechtlichen Vorschriften zu versteuern.
      </p>

      <h2>§ 8 Haftung</h2>
      <p>
        (1) Der Anbieter haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit sowie nach dem
        Produkthaftungsgesetz. Bei leicht fahrlässiger Verletzung wesentlicher Vertragspflichten
        (Kardinalpflichten) ist die Haftung auf den vertragstypischen, vorhersehbaren Schaden
        begrenzt.
      </p>
      <p>
        (2) Die Haftung des Anbieters für die Richtigkeit der durch den KI-Schwarm erzeugten
        Prognosen ist ausgeschlossen, soweit dies gesetzlich zulässig ist. Der Anbieter
        garantiert insbesondere keine bestimmte Treffergenauigkeit der Urteilsprognose.
      </p>

      <h2>§ 9 Laufzeit und Kündigung</h2>
      <p>
        Der Nutzungsvertrag wird auf unbestimmte Zeit geschlossen und kann von beiden Seiten
        jederzeit ohne Einhaltung einer Frist in Textform gekündigt werden. Bereits bezahlte,
        nicht verbrauchte Leistungen werden anteilig erstattet.
      </p>

      <h2>§ 10 Änderungen der AGB</h2>
      <p>
        Der Anbieter kann diese AGB mit Wirkung für die Zukunft ändern. Die Änderungen werden dem
        Kunden mindestens vier Wochen vor ihrem Inkrafttreten per E-Mail angekündigt. Widerspricht
        der Kunde den Änderungen nicht innerhalb dieser Frist in Textform, gelten die Änderungen
        als angenommen. Der Anbieter weist hierauf im Ankündigungstext gesondert hin.
      </p>

      <h2>§ 11 Schlussbestimmungen</h2>
      <p>
        (1) Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.
        Für Verbraucher gilt dies nur, soweit dadurch nicht zwingende Vorschriften des
        Verbraucherschutzrechts ihres gewöhnlichen Aufenthalts ausgeschlossen werden.
      </p>
      <p>
        (2) Ausschließlicher Gerichtsstand für Kaufleute ist der Sitz des Anbieters.
      </p>
      <p>
        (3) Sollten einzelne Bestimmungen dieser AGB unwirksam sein, bleibt die Wirksamkeit der
        übrigen Bestimmungen unberührt. An die Stelle der unwirksamen Bestimmung tritt die
        gesetzliche Regelung.
      </p>
    </LegalLayout>
  );
}
